<script lang="ts" setup>
import type {
  LiftPlan,
  SaveLiftPlanUEData,
} from '@/components/common/projects/lift-plan-ue/logic/useLiftPlanUELogic';
import { models } from 'cesg-shared';
import type { SessionUser } from 'cesg-shared/dist/models';
import { computed } from 'vue';
import LiftPlanUEList, {
  type LiftPlanUEListProps,
} from '../LiftPlanUEList.vue';
import LiftPlanUEListSpinner from './LiftPlanUEListSpinner.vue';
import { useLiftPlanUECreateMutation } from './useLiftPlanUECreateMutation.ts';
import { useLiftPlanUEDeleteMutation } from './useLiftPlanUEDeleteMutation.ts';
import { useLiftPlanUEListQuery } from './useLiftPlanUEListQuery.ts';
import { useLiftPlanUEUpdateMutation } from './useLiftPlanUEUpdateMutation.ts';

const props = defineProps<{
  projectId: string;
  user: SessionUser;
}>();

const { data, isLoading } = useLiftPlanUEListQuery(props.projectId);
const { mutateAsync: createLiftPlanUE, isPending: isCreating } =
  useLiftPlanUECreateMutation(props.projectId);
const { mutateAsync: deleteLiftPlanUE } = useLiftPlanUEDeleteMutation(
  props.projectId,
);
const {
  mutateAsync: updateLiftPlanUE,
  isPending: isSavingPending,
  variables: savingVariables,
} = useLiftPlanUEUpdateMutation(props.projectId);

function toFrontendLiftPlan(
  api: models.domain.LiftPlanUE,
): LiftPlan & { id: string } {
  return {
    id: api.id,
    name: api.name,
    contractSum: api.contractSum,
    retentionPercentage: api.retentionPercentage,
    totalAmount: 0,
    totalRetention: 0,
    totalAmountDue: 0,
    entreprenadsumma: 0,
    entries: api.entries.map((e) => ({
      id: e.id,
      number: e.number,
      description: e.description ?? undefined,
      dueDate: e.dueDate ? new Date(e.dueDate) : undefined,
      amount: e.amount,
      retention: e.retention,
      amountDue: e.amountDue,
      invoicedDate: e.invoicedDate ? new Date(e.invoicedDate) : undefined,
      paymentDate: e.paymentDate ? new Date(e.paymentDate) : undefined,
      comment: e.comment ?? undefined,
    })),
    subtotalRows: api.subtotals.map((s) => ({
      id: s.id,
      label: s.description,
      amount: s.amount,
      invoicedDate: s.invoicedDate ? new Date(s.invoicedDate) : undefined,
      paymentDate: s.paymentDate ? new Date(s.paymentDate) : undefined,
      comment: s.comment ?? undefined,
    })),
    ataRows: api.atas.map((a) => ({
      id: a.id,
      label: a.description,
      amount: a.amount,
      invoicedDate: a.invoicedDate ? new Date(a.invoicedDate) : undefined,
      paymentDate: a.paymentDate ? new Date(a.paymentDate) : undefined,
      comment: a.comment ?? undefined,
    })),
  };
}

const liftPlanUEListData = computed((): LiftPlanUEListProps => {
  const q = data.value;
  return {
    initialLiftPlans: (q ?? []).map(toFrontendLiftPlan),
    user: props.user,
    projectId: props.projectId,
    isCreating: isCreating.value,
    isSavingId: isSavingPending.value
      ? savingVariables.value?.liftPlanId
      : undefined,
  };
});

function toBody(saveData: SaveLiftPlanUEData): models.PutLiftPlanUEBody {
  return {
    name: saveData.name,
    contractSum: saveData.contractSum,
    retentionPercentage: saveData.retentionPercentage,
    entries: saveData.entries.map((e) => ({
      number: e.number,
      description: e.description ?? null,
      dueDate: e.dueDate?.toISOString() ?? null,
      amount: e.amount,
      retention: e.retention,
      amountDue: e.amountDue,
      invoicedDate: e.invoicedDate?.toISOString() ?? null,
      paymentDate: e.paymentDate?.toISOString() ?? null,
      comment: e.comment ?? null,
    })),
    subtotals: saveData.subtotalRows.map((r) => ({
      description: r.label,
      amount: r.amount,
      invoicedDate: r.invoicedDate?.toISOString() ?? null,
      paymentDate: r.paymentDate?.toISOString() ?? null,
      comment: r.comment ?? null,
    })),
    atas: saveData.ataRows.map((r) => ({
      description: r.label,
      amount: r.amount,
      invoicedDate: r.invoicedDate?.toISOString() ?? null,
      paymentDate: r.paymentDate?.toISOString() ?? null,
      comment: r.comment ?? null,
    })),
  };
}

async function handleCreate() {
  await createLiftPlanUE();
}

async function handleDelete(liftPlanId: string) {
  await deleteLiftPlanUE({ liftPlanId });
}

async function handleSave(liftPlanId: string, saveData: SaveLiftPlanUEData) {
  await updateLiftPlanUE({ liftPlanId, body: toBody(saveData) });
}
</script>

<template>
  <LiftPlanUEListSpinner v-if="isLoading" />
  <LiftPlanUEList
    v-else
    v-bind="liftPlanUEListData"
    @create="handleCreate"
    @delete="handleDelete"
    @save="handleSave"
  />
</template>
